import { React } from 'Imports';
import DropdownSwitch from '$Components/FilterComponents/Filters/DropdownSwitch';
import { InputAdornment, TextField } from '@mui/material';
import { VehicleCameraPairingResponse } from '$Generated/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faXmark, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

interface ISelectCameraStepStyles {
    content: string;
    searchInputContainer: string;
    searchInput: string;
    searchIcon: string;
    note: string;
    noteLabel: string;
    titleDescriptionWrapper: string;
    verbiage: string;
    cameraList: string;
    cameraTile: string;
    cameraTileSelected: string;
    cameraInfo: string;
    cameraSerial: string;
    vehicleName: string;
    statusIndicator: string;
    onlineIndicator: string;
    offlineIndicator: string;
    onlineText: string;
    offlineText: string;
    noResults: string;
    onlineToggleText: string;
}

interface ISelectCameraStepProps {
    filteredPairings: VehicleCameraPairingResponse[];
    selectedPairingId: number | null;
    onSelectPairing: (id: number | null) => void;
    searchText: string;
    onSearchTextChange: (s: string) => void;
    onSearchClear: () => void;
    onlineOnly: boolean;
    onOnlineOnlyToggle: () => void;
    isEncompass: boolean;
    styles: ISelectCameraStepStyles;
}

export function SelectCameraStep(props: ISelectCameraStepProps) {
    const {
        filteredPairings,
        selectedPairingId,
        onSelectPairing,
        searchText,
        onSearchTextChange,
        onSearchClear,
        onlineOnly,
        onOnlineOnlyToggle,
        isEncompass,
        styles,
    } = props;

    return (
        <div className={styles.content}>
            <div className={styles.titleDescriptionWrapper}>
                <b>Select a Camera</b>
                <p className={styles.verbiage}>
                    If requesting footage from an offline camera, the system will attempt to wake it upon request {'\u2014'} otherwise, footage
                    will be retrieved the next time the camera comes online.
                </p>
            </div>

            <div className={styles.note}>
                {isEncompass && <FontAwesomeIcon icon={faExclamationCircle} fontSize={'16px'} style={{ marginRight: '-14px' }} />}
                <span>
                    <span className={styles.noteLabel}>Note:</span> Video recalls will not include location information.
                </span>
            </div>

            <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search by camera serial # or vehicle name ..."
                value={searchText}
                onChange={(e) => onSearchTextChange(e.target.value)}
                inputProps={{ className: styles.searchInput }}
                InputProps={{
                    className: styles.searchInputContainer,
                    startAdornment: isEncompass ? (
                        <InputAdornment position="start">
                            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                        </InputAdornment>
                    ) : undefined,
                    endAdornment: searchText ? (
                        <InputAdornment position="end" sx={{ cursor: 'pointer' }} onClick={onSearchClear}>
                            <FontAwesomeIcon icon={faXmark} />
                        </InputAdornment>
                    ) : !isEncompass ? (
                        <InputAdornment position="end">
                            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                        </InputAdornment>
                    ) : undefined,
                }}
            />

            <div style={{ marginLeft: '-8px', marginTop: isEncompass ? '-12px' : '-5px' }}>
                <DropdownSwitch
                    checked={onlineOnly}
                    onChange={onOnlineOnlyToggle}
                    toggleText="Display Online Cameras Only"
                    textClassName={styles.onlineToggleText}
                />
            </div>

            <div className={styles.cameraList}>
                {filteredPairings.map((pairing) => {
                    const isSelected = selectedPairingId === pairing.id;
                    const online = pairing.cameraDevice?.onlineStatus?.toLowerCase() === 'online';

                    return (
                        <div
                            key={pairing.id}
                            className={`${styles.cameraTile}${isSelected ? ` ${styles.cameraTileSelected}` : ''}`}
                            onClick={() => onSelectPairing(pairing.id ?? null)}
                        >
                            <div className={styles.cameraInfo}>
                                <div className={styles.cameraSerial}>{pairing.cameraDevice?.serialNumber}</div>
                                <div className={styles.vehicleName}>{pairing.vehicle?.name}</div>
                            </div>
                            <div className={`${styles.statusIndicator} ${online ? styles.onlineIndicator : styles.offlineIndicator}`}>
                                <span className={online ? styles.onlineText : styles.offlineText}>{online ? 'Online' : 'Offline'}</span>
                            </div>
                        </div>
                    );
                })}
                {filteredPairings.length === 0 && <div className={styles.noResults}>No cameras found</div>}
            </div>
        </div>
    );
}
